// ============================================
// APPLICATIONS LIST PAGE
// i18n: Faz 6D (v2.6)
// ============================================
// TIER-4 UPDATE:
// [ADMİN-H5] Başvuru durumları filtresi çalışır hale getirildi
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  MoreVertical,
  Eye,
  Briefcase,
  User,
  Calendar,
} from 'lucide-react';
import type { ApplicationStatus } from '@crewatsea/shared-types';
import {
  PageHeader,
  DataTable,
  FilterBar,
  FilterSelect,
  StatusBadge,
} from '@/components';
import { adminApi } from '@/services/api';
import { formatRelativeTime, getInitials, cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/applications/')({
  component: ApplicationsPage,
});

function ApplicationsPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // [ADMİN-H5] Başvuruları filtrelerle getir
  const { data: rawData, isLoading } = useQuery({
    queryKey: ['admin', 'applications', { page, limit, status: statusFilter, search }],
    queryFn: async () => {
      const response = await adminApi.getApplications({
        page,
        limit,
        status: statusFilter || undefined,
        search: search || undefined,
      });
      return (response as any)?.data || response;
    },
  });

  const data = rawData;

  const columns = [
    {
      key: 'seafarer',
      header: t('panel:applications.applicant'),
      render: (app: any) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-ocean-100 text-ocean-700 font-medium">
            {app.seafarer
              ? getInitials(app.seafarer.firstName, app.seafarer.lastName)
              : '??'}
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {app.seafarer
                ? `${app.seafarer.firstName} ${app.seafarer.lastName}`
                : '-'}
            </p>
            <p className="text-xs text-gray-500">
              {app.seafarer?.currentRank?.nameTr || app.seafarer?.currentRank?.nameEn || '-'}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'job',
      header: t('panel:applications.job'),
      render: (app: any) => (
        <div>
          <div className="flex items-center gap-1">
            <Briefcase className="h-4 w-4 text-gray-400" />
            <p className="font-medium text-gray-900">{app.job?.title || '-'}</p>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {app.job?.company?.name || '-'}
          </p>
        </div>
      ),
    },
    {
      key: 'matchScore',
      header: t('panel:applications.matchScore'),
      render: (app: any) => (
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full',
                (app.matchScore || 0) >= 70
                  ? 'bg-green-500'
                  : (app.matchScore || 0) >= 50
                    ? 'bg-amber-500'
                    : 'bg-red-500'
              )}
              style={{ width: `${app.matchScore || 0}%` }}
            />
          </div>
          <span className="text-sm font-medium text-gray-700">
            {app.matchScore || 0}%
          </span>
        </div>
      ),
    },
    {
      key: 'expectedSalary',
      header: t('panel:applications.expectedSalary'),
      render: (app: any) => (
        <span className="text-gray-600">
          {app.expectedSalary
            ? `$${app.expectedSalary.toLocaleString('tr-TR')}`
            : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('common:status'),
      render: (app: any) => <StatusBadge status={app.status} dot />,
    },
    {
      key: 'date',
      header: t('panel:applications.date'),
      render: (app: any) => (
        <div className="flex items-center gap-1 text-gray-500">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span>{formatRelativeTime(app.createdAt)}</span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (app: any) => (
        <div className="relative group">
          <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md">
            <MoreVertical className="h-4 w-4" />
          </button>
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-maritime shadow-maritime-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
            <div className="py-1">
              <Link
                to={`/applications/${app.id}` as any}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Eye className="h-4 w-4" />
                {t('common:view')}
              </Link>
              <Link
                to={`/seafarers/${app.seafarerId}` as any}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <User className="h-4 w-4" />
                {t('panel:applications.seafarerProfile')}
              </Link>
              <Link
                to={`/jobs/${app.jobId}` as any}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Briefcase className="h-4 w-4" />
                {t('panel:applications.jobDetail')}
              </Link>
            </div>
          </div>
        </div>
      ),
    },
  ];

  // [ADMİN-H5] Tüm başvuru durumları - enum key'lerinden
  const statusOptions = [
    { value: 'PENDING', label: t('enums:applicationStatus.PENDING') },
    { value: 'REVIEWED', label: t('enums:applicationStatus.REVIEWED') },
    { value: 'SHORTLISTED', label: t('enums:applicationStatus.SHORTLISTED') },
    { value: 'INTERVIEW', label: t('enums:applicationStatus.INTERVIEW') },
    { value: 'OFFERED', label: t('enums:applicationStatus.OFFERED') },
    { value: 'ACCEPTED', label: t('enums:applicationStatus.ACCEPTED') },
    { value: 'REJECTED', label: t('enums:applicationStatus.REJECTED') },
    { value: 'WITHDRAWN', label: t('enums:applicationStatus.WITHDRAWN') },
  ];

  const activeFiltersCount = (statusFilter ? 1 : 0) + (search ? 1 : 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('panel:applications.title')}
        description={t('panel:applications.description')}
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('panel:applications.searchPlaceholder')}
        filters={
          <FilterSelect
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            placeholder={t('panel:applications.allStatuses')}
          />
        }
        activeFiltersCount={activeFiltersCount}
        onClearFilters={() => {
          setSearch('');
          setStatusFilter('');
        }}
      />

      <DataTable
        columns={columns}
        data={data?.data || []}
        keyExtractor={(app) => app.id}
        loading={isLoading}
        emptyMessage={t('panel:applications.noApplications')}
        pagination={{
          page,
          limit,
          total: data?.meta?.total || 0,
          totalPages: data?.meta?.totalPages || 1,
          onPageChange: setPage,
          onLimitChange: setLimit,
        }}
      />
    </div>
  );
}
